import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {Spinner} from 'react-bootstrap'
import {KTIcon} from '../../../_metronic/helpers/components/KTIcon'
import {PageTitle} from '../../../_metronic/layout/core'
import BedRockContext from '../../BedRockContext'
import {getHelpDocument, searchHelp, getAllHelpDocuments, getHelpLanguages} from '../generic/crud'
import {HelpSidebar} from './HelpSidebar'
import {HelpDocumentView} from './HelpDocumentView'
import {HelpSearchResults} from './HelpSearchResults'
import {HelpWelcome} from './HelpWelcome'
import type {HelpDocument, Language} from './HelpDocumentView'
import {Offcanvas} from 'react-bootstrap'
import './help.scss'

interface Category {
  name: string
  displayName: string
  icon: string
  order: number
  documents: Array<{
    pageId: string
    title: string
    shortDescription: string
    order: number
    icon: string
  }>
}

interface SearchResult {
  pageId: string
  title: string
  shortDescription: string
  category: string
  icon: string
  matchSnippet: string
}

const STORAGE_KEY = 'help_language'

const HelpPage: React.FC = () => {
  const {pageId} = useParams<{pageId: string}>()
  const navigate = useNavigate()
  const {setBaseData} = useContext(BedRockContext) as any

  const [currentLang, setCurrentLang] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) || 'en'
  )
  const [languages, setLanguages] = useState<Language[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [document, setDocument] = useState<HelpDocument | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isLoadingDoc, setIsLoadingDoc] = useState(false)
  const [isLoadingSearch, setIsLoadingSearch] = useState(false)
  const [isLoadingAll, setIsLoadingAll] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSidebar, setShowSidebar] = useState(false)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Set page toolbar
  useEffect(() => {
    setBaseData({title: 'Help & Documentation'})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  // Load languages + all docs on mount. Sidebar/menu is always English regardless of reading language.
  useEffect(() => {
    loadInitialData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Load specific document when pageId changes
  useEffect(() => {
    if (pageId) {
      loadDocument(pageId)
      setSearchQuery('')
      setSearchResults([])
    } else {
      setDocument(null)
    }
  }, [pageId, currentLang]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadInitialData = async () => {
    setIsLoadingAll(true)
    try {
      const [langRes, allRes] = await Promise.all([
        getHelpLanguages(),
        getAllHelpDocuments('en'),
      ])
      setLanguages(langRes.data || [])
      setCategories(allRes.data?.categories || [])
    } catch (_err) {
      // Silently handle - help data is non-critical
    } finally {
      setIsLoadingAll(false)
    }
  }

  const loadDocument = async (id: string) => {
    setIsLoadingDoc(true)
    setError(null)
    try {
      const res = await getHelpDocument(id, currentLang)
      setDocument(res.data)
    } catch (err: any) {
      setError(err?.response?.status === 404
        ? 'This help article was not found.'
        : 'Failed to load help article. Please try again.'
      )
      setDocument(null)
    } finally {
      setIsLoadingDoc(false)
    }
  }

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query)
      if (debounceRef.current) clearTimeout(debounceRef.current)

      if (!query.trim()) {
        setSearchResults([])
        setIsLoadingSearch(false)
        return
      }

      setIsLoadingSearch(true)
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await searchHelp(query, currentLang)
          setSearchResults(res.data || [])
        } catch (_err) {
          setSearchResults([])
        } finally {
          setIsLoadingSearch(false)
        }
      }, 300)
    },
    [currentLang]
  )

  const handleLangChange = (lang: string) => {
    setCurrentLang(lang)
    localStorage.setItem(STORAGE_KEY, lang)
  }

  // Derive the "back" path from pageId
  const backPath = useMemo(() => {
    if (!pageId) return null
    // Reverse lookup: find the URL for this pageId
    const map: Record<string, string> = {
      'dashboard': '/dashboard',
      'pending-sales': '/pending-sales',
      'sales-list': '/sales-list',
      'sale-new': '/sale-new',
      'purchase-list': '/purchases-list',
      'purchase-new': '/purchases-list',
      'users': '/users',
      'roles-permissions': '/roles',
      'vehicles': '/vehicles',
      'sales-customers': '/sales-customers',
      'suppliers': '/suppliers',
      'inventory-items': '/inventory-items',
      'service-items': '/service-items',
      'ledger-accounts': '/ledger-accounts',
      'expenses': '/expenses',
      'daily-report': '/daily-report',
      'org-settings': '/org-settings',
      'ui-settings': '/ui-settings',
      'weighbridge-setup': '/weighbridge-setup',
      'wifi-settings': '/wifi-settings',
      'sync': '/sync',
      'captured-images': '/captured-images',
      'other-settings': '/other-settings',
    }
    return map[pageId] || null
  }, [pageId])

  const isSearching = searchQuery.trim().length > 0

  // Main content area
  const renderContent = () => {
    if (isSearching) {
      return (
        <HelpSearchResults
          results={searchResults}
          query={searchQuery}
          isLoading={isLoadingSearch}
        />
      )
    }

    if (pageId) {
      if (isLoadingDoc) {
        return (
          <div className='text-center py-10'>
            <Spinner animation='border' variant='primary' />
            <p className='text-muted mt-3'>Loading article...</p>
          </div>
        )
      }
      if (error) {
        return (
          <div className='text-center py-10'>
            <KTIcon iconName='information' className='fs-2tx text-warning mb-4 d-block' />
            <h5 className='text-gray-700'>{error}</h5>
            <button className='btn btn-sm btn-primary mt-3' onClick={() => navigate('/help')}>
              Browse all articles
            </button>
          </div>
        )
      }
      if (document) {
        return (
          <HelpDocumentView
            document={document}
            languages={languages}
            currentLang={currentLang}
            onLangChange={handleLangChange}
          />
        )
      }
    }

    return <HelpWelcome categories={categories} isLoading={isLoadingAll} />
  }

  return (
    <>
      <PageTitle>Help & Documentation</PageTitle>
      <div className='help-page'>
        {/* Header bar */}
        <div className='help-header mb-5'>
          <button
            className='btn btn-sm btn-light-primary'
            onClick={() => (backPath ? navigate(backPath) : navigate(-1))}
            aria-label='Go back'
          >
            <KTIcon iconName='left' className='fs-5 me-1' />
            Back
          </button>

          <button
            className='btn btn-sm btn-light-primary'
            onClick={() => navigate('/help')}
            aria-label='Documentation home'
          >
            <KTIcon iconName='home-2' className='fs-5 me-1' />
            Home
          </button>

          {!isLoadingAll && categories.length > 0 && (
            <button
              className='btn btn-sm btn-light-primary'
              onClick={() => setShowSidebar(true)}
              aria-label='Browse articles'
            >
              <KTIcon iconName='menu' className='fs-5 me-1' />
              Browse
            </button>
          )}

          <div className='flex-grow-1' />

          <div className='position-relative help-search-input'>
            <KTIcon
              iconName='magnifier'
              className='fs-4 text-gray-500 position-absolute top-50 translate-middle-y ms-4'
            />
            <input
              type='text'
              className='form-control form-control-sm ps-11'
              placeholder='Search help articles...'
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              aria-label='Search help articles'
              role='searchbox'
            />
            {searchQuery && (
              <button
                className='btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-1'
                onClick={() => handleSearch('')}
                aria-label='Clear search'
              >
                <KTIcon iconName='cross' className='fs-5 text-gray-500' />
              </button>
            )}
          </div>
        </div>

        {/* Sidebar as Offcanvas */}
        <Offcanvas show={showSidebar} onHide={() => setShowSidebar(false)} placement='start'>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className='fw-bold'>Documentation</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className='p-0'>
            <div onClick={() => setShowSidebar(false)}>
              <HelpSidebar categories={categories} activePageId={pageId} />
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        {/* Content area - full width */}
        <div className='help-content mx-auto'>{renderContent()}</div>
      </div>
    </>
  )
}

export {HelpPage}
